import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';
import MainLayout from '../../components/layout/MainLayout';
import logApi from '../../services/api/log.api';
import TimeRangeSelector, { TimeRange } from '../../components/common/TimeRangeSelector';
import AutoRefreshToggle, { AutoRefreshConfig } from '../../components/common/AutoRefreshToggle';

export default function LogsPage() {
  const { t } = useTranslation(['logs', 'common']);
  const navigate = useNavigate();
  const selectedAppId = useAppSelector(selectCurrentApplication);

  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalLogs, setTotalLogs] = useState(0);
  const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set());
  const [showCollectionMethods, setShowCollectionMethods] = useState<boolean>(true);

  // Time range and auto-refresh
  const [timeRange, setTimeRange] = useState<TimeRange>({
    startTime: Date.now() - 60 * 60 * 1000, // Last hour
    endTime: Date.now(),
    rangeType: 'preset',
    presetValue: '1h',
  });
  const [autoRefresh, setAutoRefresh] = useState<AutoRefreshConfig>({
    enabled: false,
    interval: 30000, // 30 seconds
  });
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  useEffect(() => {
    setCurrentPage(1);
    loadLogs();
  }, [selectedAppId, levelFilter, timeRange]);

  useEffect(() => {
    loadLogs();
  }, [currentPage, pageSize]);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh.enabled) return;

    const interval = setInterval(() => {
      // Update time range if using preset
      if (timeRange.rangeType === 'preset' && timeRange.presetValue) {
        const endTime = Date.now();
        let startTime = endTime;

        const ranges: Record<string, number> = {
          '15m': 15 * 60 * 1000,
          '1h': 60 * 60 * 1000,
          '6h': 6 * 60 * 60 * 1000,
          '12h': 12 * 60 * 60 * 1000,
          '24h': 24 * 60 * 60 * 1000,
          '7d': 7 * 24 * 60 * 60 * 1000,
          '30d': 30 * 24 * 60 * 60 * 1000,
        };

        startTime = endTime - (ranges[timeRange.presetValue] || ranges['1h']);

        setTimeRange({
          ...timeRange,
          startTime,
          endTime,
        });
      } else {
        // For custom ranges, just reload
        loadLogs();
      }
    }, autoRefresh.interval);

    return () => clearInterval(interval);
  }, [autoRefresh, timeRange]);

  const loadLogs = async () => {
    try {
      setLoading(true);

      const appId = selectedAppId || 'all';
      const response = await logApi.getAll(appId, {
        startTime: timeRange.startTime,
        endTime: timeRange.endTime,
        level: levelFilter !== 'all' ? levelFilter : undefined,
        search: searchQuery || undefined,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize
      });

      if (response.success) {
        setTotalLogs(response.meta.total);
        setLogs(response.data.map((log: any) => ({
          id: log.id,
          level: log.level,
          timestamp: new Date(log.timestamp).toLocaleString(),
          service: log.source || 'unknown',
          message: log.message,
          meta: log.attributes || {}
        })));
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Error loading logs:', error);
      setLogs([]);
      setTotalLogs(0);
    } finally {
      setLoading(false);
    }
  };


  const filteredLogs = logs.filter((log) => {
    const matchesSearch = !searchQuery ||
      log.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.service.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLevel = levelFilter === 'all' || log.level === levelFilter;
    const matchesService = serviceFilter === 'all' || log.service === serviceFilter;
    return matchesSearch && matchesLevel && matchesService;
  });

  const services = [...new Set(logs.map((log) => log.service))];

  const toggleLogExpansion = (index: number) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedLogs(newExpanded);
  };

  const isLogExpanded = (index: number) => expandedLogs.has(index);
  const shouldTruncate = (message: string) => message.length > 150;

  return (
    <MainLayout>
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('logs:title')}</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">{t('logs:subtitle')}</p>
      </div>

      {/* Log Collection Methods - Collapsible Section */}
      <div className="mb-8 bg-gradient-to-r from-purple-50 via-indigo-50 to-blue-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header with Toggle */}
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/50 dark:hover:bg-gray-700/50 transition-colors"
          onClick={() => setShowCollectionMethods(!showCollectionMethods)}
        >
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              🚀 {t('logs:collectionMethods.title')}
            </h2>
            <span className="text-xs px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full">
              2 {t('logs:collectionMethods.subtitle').toLowerCase().includes('método') ? 'métodos' : 'methods'}
            </span>
          </div>
          <button className="p-2 hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-colors">
            <svg
              className={`h-5 w-5 text-gray-600 dark:text-gray-400 transition-transform ${showCollectionMethods ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Collapsible Content */}
        {showCollectionMethods && (
          <div className="px-6 pb-6">

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Method 1: Code/SDK */}
          <div
            onClick={() => navigate('/logs/integration')}
            className="group relative cursor-pointer overflow-hidden rounded-xl bg-white dark:bg-gray-900 p-6 border-2 border-purple-200 dark:border-purple-700 transition-all hover:shadow-2xl hover:scale-[1.02] hover:border-purple-400"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 p-3 shadow-lg group-hover:scale-110 transition-transform">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  📝 Método 1: Código (SDK/API)
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  Envie logs diretamente do seu código usando nossa API REST. Ideal para aplicações web, mobile e backend.
                </p>
                <div className="inline-flex items-center text-sm font-semibold text-purple-600 dark:text-purple-400 group-hover:text-purple-700">
                  Ver Exemplos de Código
                  <svg className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Method 2: Agent */}
          <div
            onClick={() => navigate('/agents/logs/install')}
            className="group relative cursor-pointer overflow-hidden rounded-xl bg-white dark:bg-gray-900 p-6 border-2 border-indigo-200 dark:border-indigo-700 transition-all hover:shadow-2xl hover:scale-[1.02] hover:border-indigo-400"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 rounded-lg bg-gradient-to-br from-indigo-600 to-blue-600 p-3 shadow-lg group-hover:scale-110 transition-transform">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  🤖 Método 2: Agente (Automático)
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  Instale nosso agente para coletar logs automaticamente dos seus servidores e aplicações sem modificar código.
                </p>
                <div className="inline-flex items-center text-sm font-semibold text-indigo-600 dark:text-indigo-400 group-hover:text-indigo-700">
                  Configurar Agente
                  <svg className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
            </div>
          </div>
        )}
      </div>

      {/* Time Range Selector */}
      <div className="mb-6">
        <TimeRangeSelector
          value={timeRange}
          onChange={setTimeRange}
        />
      </div>

      {/* Auto Refresh Toggle */}
      <div className="mb-6">
        <AutoRefreshToggle
          value={autoRefresh}
          onChange={setAutoRefresh}
          onManualRefresh={loadLogs}
          lastUpdated={lastUpdated}
        />
      </div>

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <input
          type="text"
          placeholder={t('logs:searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && loadLogs()}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white"
        />
        <select
          value={levelFilter}
          onChange={(e) => { setLevelFilter(e.target.value); loadLogs(); }}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
        >
          <option value="all">{t('logs:filters.allLevels')}</option>
          <option value="error">{t('logs:levels.error')}</option>
          <option value="warn">{t('logs:levels.warn')}</option>
          <option value="info">{t('logs:levels.info')}</option>
        </select>
        <select
          value={serviceFilter}
          onChange={(e) => setServiceFilter(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
        >
          <option value="all">{t('logs:filters.allServices')}</option>
          {services.map((service) => (
            <option key={service} value={service}>{service}</option>
          ))}
        </select>
      </div>

      {/* Log List */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="flex justify-center items-center py-12 text-gray-500 dark:text-gray-400">
            No logs found
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200 dark:divide-gray-800">
              {filteredLogs.map((log, i) => {
                const isExpanded = isLogExpanded(i);
                const needsTruncation = shouldTruncate(log.message);
                const displayMessage = !isExpanded && needsTruncation
                  ? log.message.substring(0, 150) + '...'
                  : log.message;
                const hasMetadata = Object.keys(log.meta).length > 0;

                return (
                  <div key={i} className="p-4 font-mono text-sm hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <div className="flex items-start space-x-4">
                      <span className={`inline-flex shrink-0 rounded px-2 py-1 text-xs font-semibold ${
                        log.level === 'error' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                        log.level === 'warn' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                        'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                      }`}>
                        {log.level.toUpperCase()}
                      </span>
                      <span className="shrink-0 text-gray-500 dark:text-gray-400">{log.timestamp}</span>
                      <span className="shrink-0 rounded bg-gray-100 px-2 py-1 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300">{log.service}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-900 dark:text-white break-words whitespace-pre-wrap">
                          {displayMessage}
                        </p>
                        {(needsTruncation || hasMetadata) && (
                          <button
                            onClick={() => toggleLogExpansion(i)}
                            className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium"
                          >
                            {isExpanded ? '▼ Show less' : '▶ Show more'}
                          </button>
                        )}
                      </div>
                    </div>
                    {isExpanded && hasMetadata && (
                      <div className="ml-12 mt-3 rounded bg-gray-50 p-3 dark:bg-gray-900/50">
                        <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Metadata:</div>
                        <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
                          {Object.entries(log.meta).map(([key, value]) => (
                            <div key={key} className="flex">
                              <span className="font-semibold text-gray-700 dark:text-gray-300 w-32 shrink-0">{key}:</span>
                              <span className="break-all">{typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-gray-800/50">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalLogs)} of {totalLogs} logs
                </span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="ml-4 rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  Previous
                </button>
                <span className="flex items-center px-3 text-sm text-gray-700 dark:text-gray-300">
                  Page {currentPage} of {Math.ceil(totalLogs / pageSize)}
                </span>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage >= Math.ceil(totalLogs / pageSize)}
                  className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(Math.ceil(totalLogs / pageSize))}
                  disabled={currentPage >= Math.ceil(totalLogs / pageSize)}
                  className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  Last
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
}
